import type { CompanyInputT, RoastResultT } from "../schema/roastSchema";

/**
 * Pure UI state machine for the Marketing Roast Machine.
 *
 * This module is strictly logic: no DOM imports, no timers, no `Date.now()`.
 * All timing concerns (when to schedule a REVEAL_TICK, when to fire TIMEOUT,
 * what `startedAt` to stamp on `loading`/`RETRY`) live in the caller
 * (see `appController.ts`). The reducer here is a total function over
 * `(AppState, AppEvent)` that returns the next `AppState`.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "App State (UI)" and
 * "State Machine".
 */

/**
 * Discriminated union of all UI states.
 *
 * - `idle`: entry screen, no pending work, no retained input or result.
 * - `loading`: waiting for the proxy to return a validated Roast_Result.
 * - `revealing`: stepping through the staggered reveal of the result.
 * - `result`: full result on screen, awaiting RESET.
 * - `error`: something went wrong; `kind2` distinguishes timeout vs error.
 */
export type AppState =
  | { kind: "idle" }
  | { kind: "loading"; input: CompanyInputT; startedAt: number }
  | {
      kind: "revealing";
      input: CompanyInputT;
      result: RoastResultT;
      revealedRecs: 0 | 1 | 2 | 3;
      skipRequested: boolean;
    }
  | { kind: "result"; input: CompanyInputT; result: RoastResultT }
  | { kind: "error"; input: CompanyInputT; kind2: "timeout" | "error" };

/**
 * Events that drive state transitions.
 *
 * Payloads:
 * - `SUBMIT`: validated input plus the caller-stamped `startedAt`.
 * - `RESULT_READY`: the validated Roast_Result from the proxy.
 * - `RETRY`: a fresh `startedAt`; the input is reused from the error state.
 * All other events are payload-free.
 */
export type AppEvent =
  | { type: "SUBMIT"; input: CompanyInputT; startedAt: number }
  | { type: "RESULT_READY"; result: RoastResultT }
  | { type: "ERROR" }
  | { type: "TIMEOUT" }
  | { type: "REVEAL_TICK" }
  | { type: "SKIP" }
  | { type: "ALL_REVEALED" }
  | { type: "RETRY"; startedAt: number }
  | { type: "RESET" };

/**
 * A fresh canonical `idle` state.
 *
 * Returns a new object on every call so callers can safely compare post-reset
 * states by structural equality without worrying about retained `input` or
 * `result` fields (Property 8 — reset clean-state invariant).
 */
export function canonicalIdle(): AppState {
  return { kind: "idle" };
}

/** Initial state for a freshly mounted app. */
export const initialState: AppState = canonicalIdle();

/**
 * Pure reducer over `(AppState, AppEvent)`.
 *
 * - No side effects, no timer APIs, no `Date.now()`.
 * - Events that don't apply to the current state return the state unchanged.
 * - `RESET` is a universal escape hatch from any state (Property 8).
 */
export function transition(state: AppState, event: AppEvent): AppState {
  // Universal escape hatch: RESET always returns to a fresh idle.
  if (event.type === "RESET") {
    return canonicalIdle();
  }

  switch (state.kind) {
    case "idle": {
      if (event.type === "SUBMIT") {
        return {
          kind: "loading",
          input: event.input,
          startedAt: event.startedAt,
        };
      }
      return state;
    }

    case "loading": {
      if (event.type === "RESULT_READY") {
        return {
          kind: "revealing",
          input: state.input,
          result: event.result,
          revealedRecs: 0,
          skipRequested: false,
        };
      }
      if (event.type === "ERROR") {
        return { kind: "error", input: state.input, kind2: "error" };
      }
      if (event.type === "TIMEOUT") {
        return { kind: "error", input: state.input, kind2: "timeout" };
      }
      return state;
    }

    case "revealing": {
      if (event.type === "REVEAL_TICK") {
        // Cap at 3; stay put if already at the final recommendation.
        if (state.revealedRecs >= 3) {
          return state;
        }
        const next = (state.revealedRecs + 1) as 0 | 1 | 2 | 3;
        return { ...state, revealedRecs: next };
      }
      if (event.type === "SKIP") {
        // Only flag the skip; ALL_REVEALED performs the actual transition.
        if (state.skipRequested) {
          return state;
        }
        return { ...state, skipRequested: true };
      }
      if (event.type === "ALL_REVEALED") {
        return { kind: "result", input: state.input, result: state.result };
      }
      return state;
    }

    case "result": {
      // Only RESET applies here, and it's handled above.
      return state;
    }

    case "error": {
      if (event.type === "RETRY") {
        return {
          kind: "loading",
          input: state.input,
          startedAt: event.startedAt,
        };
      }
      return state;
    }
  }
}
